/**
 * Lesson Video Modal — center-screen tutorial video with close/minimize.
 * Used by checklist-mode lessons (e.g. ent-gfx-03). Can be reopened from
 * the breadcrumb or checklist after it's dismissed.
 *
 * When opened with `lockUntilEnded`, the modal cannot be dismissed until the
 * video has played through (used at lesson start to force a full watch). Once
 * it ends, a "Begin Tasks" button appears to close the modal and enter the sim.
 */

let overlay: HTMLElement | null = null;
let currentUrl: string | null = null;
let currentTitle: string | null = null;
let lockActive = false;
let lessonStart = false;

function setCloseEnabled(enabled: boolean): void {
  if (!overlay) return;
  const btn = overlay.querySelector<HTMLButtonElement>('.lesson-video-close');
  if (btn) {
    btn.disabled = !enabled;
    btn.title = enabled ? 'Close' : 'Finish the video to continue';
  }
}

function showHint(show: boolean): void {
  const hint = overlay?.querySelector<HTMLElement>('.lesson-video-hint');
  if (hint) hint.style.display = show ? '' : 'none';
}

function showBeginButton(show: boolean): void {
  const footer = overlay?.querySelector<HTMLElement>('.lesson-video-footer');
  if (footer) footer.style.display = show ? '' : 'none';
}

function handleEnded(): void {
  lockActive = false;
  setCloseEnabled(true);
  showHint(false);
  if (lessonStart) showBeginButton(true);
}

export function openLessonVideoModal(
  url: string,
  title: string = 'Tutorial',
  lockUntilEnded: boolean = false,
): void {
  currentUrl = url;
  currentTitle = title;
  lockActive = lockUntilEnded;
  lessonStart = lockUntilEnded;

  if (overlay) {
    const video = overlay.querySelector<HTMLVideoElement>('video');
    const titleEl = overlay.querySelector<HTMLElement>('.lesson-video-title');
    if (titleEl) titleEl.textContent = title;
    if (video) {
      if (video.src !== url) video.src = url;
      video.currentTime = 0;
    }
    overlay.style.display = 'flex';
    setCloseEnabled(!lockActive);
    showHint(lockActive);
    showBeginButton(false);
    if (video && video.paused) video.play().catch(() => {});
    return;
  }

  overlay = document.createElement('div');
  overlay.className = 'lesson-video-modal lockdown-allowed';
  overlay.innerHTML = `
    <div class="lesson-video-backdrop"></div>
    <div class="lesson-video-card">
      <div class="lesson-video-header">
        <span class="lesson-video-title">${title}</span>
        <span class="lesson-video-hint">Please watch the full video to continue</span>
        <button class="lesson-video-close" title="Close">&times;</button>
      </div>
      <video class="lesson-video-player" src="${url}" controls autoplay playsinline></video>
      <div class="lesson-video-footer">
        <button class="lesson-video-begin">Begin Tasks &rarr;</button>
      </div>
    </div>
  `;

  overlay.querySelector('.lesson-video-close')!.addEventListener('click', () => {
    if (lockActive) return;
    closeLessonVideoModal();
  });
  overlay.querySelector('.lesson-video-backdrop')!.addEventListener('click', () => {
    if (lockActive) return;
    closeLessonVideoModal();
  });
  overlay.querySelector('.lesson-video-begin')!.addEventListener('click', () => {
    closeLessonVideoModal();
  });
  overlay.querySelector('video')!.addEventListener('ended', handleEnded);

  document.body.appendChild(overlay);
  setCloseEnabled(!lockActive);
  showHint(lockActive);
  showBeginButton(false);
}

export function closeLessonVideoModal(): void {
  if (!overlay) return;
  if (lockActive) return;
  const video = overlay.querySelector<HTMLVideoElement>('video');
  if (video) video.pause();
  overlay.style.display = 'none';
}

export function destroyLessonVideoModal(): void {
  if (!overlay) return;
  const video = overlay.querySelector<HTMLVideoElement>('video');
  if (video) {
    video.pause();
    video.src = '';
  }
  overlay.remove();
  overlay = null;
  currentUrl = null;
  currentTitle = null;
  lockActive = false;
  lessonStart = false;
}

export function reopenLessonVideoModal(): void {
  if (currentUrl) openLessonVideoModal(currentUrl, currentTitle ?? 'Tutorial');
}
